import React, { useState, useEffect } from "react";
import { Offcanvas } from "react-bootstrap";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import { Pagination } from "swiper/modules";

import ProfileImg from "../../../assets/images/Profile.svg";
import BubbleSvgButton from "../../sharedPages/CommonComponents/BubbleSvgButton/BubbleSvgButton";
import Cross from "../../../assets/images/green-corss.svg";
import ReviewStar from "../../../assets/images/onboardingImages/ReviewStar.png";
import ReviewUser from "../../../assets/images/onboardingImages/ReviewUser.svg";
import Exchange from "../../../assets/images/onboardingImages/Exchange.svg";
import blockchainIcon from "../../../assets/images/CheckMark.svg";
import MarketPlaceRanking from "../../sharedPages/MarketPlace/MarketPlaceRanking";
// import CommunityBarChart from "../../sharedPages/MarketPlace/CommunityBarChart";
import CommunityAreaChart, { CommunityAreaChartTabs, TABS } from "../../sharedPages/MarketPlace/CommunityAreaChart";
import useApi from "../../../helpers/apiHelper/requestHelper";
import { simplifyTradingVolume } from "../../../helpers/helper";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const mapCommunitiesToTableData = (apiResponse) => {
  return apiResponse?.communities?.map((item) => {
    const stats = item.stats?.[0] || {};
    const totalReviews = item.totalReviews || 0;
    const ratingValue = `${item?.averageRating}/5`;
    const ratingSmall =
      totalReviews > 0
        ? `(${totalReviews} Review${totalReviews > 1 ? "s" : ""})`
        : "(No Reviews)";

    const pnlValue = simplifyTradingVolume(stats?.pnl) || 0;
    const winRateValue = stats.winRate || 0;
    const thisWeekPnlPercentage = simplifyTradingVolume(stats?.thisWeekPnlPercentage) || 0;

    return {
      ...item,
      name: item.disPlayName,
      rating: ratingValue,
      ratingSmall,
      pnl: {
        value: `${pnlValue}%`,
        className: pnlValue > 0 ? "greenText" : pnlValue < 0 ? "redText" : "",
      },
      winRate: {
        value: `${winRateValue}%`,
        className: winRateValue > 0 ? "greenText" : winRateValue < 0 ? "redText" : "",
      },
      aum: {
        value: `$${item.AUM || 0}`,
        className: item.AUM > 0 ? "greenText" : item.AUM < 0 ? "redText" : "",
      },
      thisWeekPnlPercentage: {
        value: thisWeekPnlPercentage,
        className:
          item.thisWeekPnlPercentage > 0 ? "greenText"
          : item.thisWeekPnlPercentage < 0 ? "redText"
          : "",
      },
      members: item.participants?.length?.toString() || "0",
      isActive: item.isActive ? "Active" : "In Active",
      communityTrial: item?.subscription?.[0]?.trialPeriodDays,
      communityMonthlyFee: item?.subscription?.[0]?.monthlyfee,
      totalReviews: item.totalReviews,
      communityRanking: item?.myRanking || apiResponse.totalCount,
      totalCommunities: apiResponse.totalCount,
    };
  });
};

// ─── Component ────────────────────────────────────────────────────────────────
const DisplayModal = ({ setChangeShowModal, communityName, userId }) => {
  const [show, setShow]               = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  // const [activeButton, setActiveButton] = useState("Day");
  const [activeTab, setActiveTab] = useState(0);
  const activeButton = TABS[activeTab]?.duration ?? "Day";

  const api = useApi();

  const handleClose = () => {
    setShow(false);
    setChangeShowModal(false);
  };

  const getStatsDataFromCard = (card) => {
    if (!card) return [];
    return [
      { title: "Win Rate",           numbers: card?.winRate?.value || "N/A" },
      { title: "Avg. Trade ROI",     numbers: `${simplifyTradingVolume(card.stats[0].roi)}%` || "N/A" },
      { title: "AUM",                numbers: card?.aum?.value || "N/A" },
      { title: "Avg. Trade Duration",numbers: "0" },
      { title: "Max. Drawdown",      numbers: "0%" },
      { title: "Avg Leverage",       numbers: card.averageLeverage?`${card?.averageLeverage}x` : "N/A" },
      { title: "Total Trades",       numbers: card.stats[0].totalTrades || "0" },
    ];
  };

  const getAllCommunities = async () => {
    const response = await api("post", "NoLinkMember/getCommunityStats", { communityName });
    if (!response.status) return;
    const data = mapCommunitiesToTableData(response?.data || []);
    setSelectedCard(data[0]);
    setShow(true);
  };

  function formatDate(dateString) {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const day = String(date.getUTCDate()).padStart(2, "0");
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const year = date.getUTCFullYear();
    return `${day}-${month}-${year}`;
  }

  useEffect(() => {
    getAllCommunities();
  }, []);

  // ── Date range label (mirrors MarketPlace) ────────────────────────────────
  const getDateRange = () => {
    const today = new Date();
    const leaderCreatedAt = selectedCard?.leaderUser?.createdAt;
    const userCreatedAt   = leaderCreatedAt ? new Date(leaderCreatedAt) : null;
    let startDate, formattedRange;

    switch (activeButton) {
      case "Day":
        return formatDate(today);
      case "Week": {
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        startDate = userCreatedAt && weekStart < userCreatedAt ? userCreatedAt : weekStart;
        return `${formatDate(startDate)} - ${formatDate(today)}`;
      }
      case "Month": {
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        startDate = userCreatedAt && monthStart < userCreatedAt ? userCreatedAt : monthStart;
        return `${formatDate(startDate)} - ${formatDate(today)}`;
      }
      case "All Time":
        return userCreatedAt
          ? `${formatDate(userCreatedAt)} - ${formatDate(today)}`
          : "N/A";
      default:
        return "N/A";
    }
  };

  return (
    <div>
      <Offcanvas
        className="CommunityOffcanvas marketPlaceCanvas"
        show={show}
        onHide={handleClose}
        placement="end"
        keyboard={false}
      >
        <Offcanvas.Body>
          <button className="closeBtn" onClick={handleClose}>
            <img src={Cross} alt="" />
          </button>

          {/* ── Top info block ─────────────────────────────────────────── */}
          <div className="infoTopBox">
            <div className="header-modal">

              {/* Left: avatar + name + trial */}
              <div className="">
                <div className="infoLeft">
                  <div className="imgBox">
                    <img src={ProfileImg} alt="" />
                  </div>
                  <div className="contantBox">
                    <p className="createdDate">{`Group created on ${formatDate(selectedCard?.createdAt)}`}</p>
                    <p className="name">{selectedCard?.disPlayName}</p>
                    <div className="trialBox">
                      <p className="fs-12">
                        <p className="fw-medium d-inline-flex">{selectedCard?.communityTrial}d</p> Trial &#8226;
                        From <span className="fs-12"><p className="fw-medium d-inline-flex">${selectedCard?.communityMonthlyFee}</p> /mo</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: ratings + blockchain + exchange */}
              <div className="">
                <div className="infoRight fs-12">
                  <div className="subAndReviewBox">
                    <div className="RevBox">
                      <img src={ReviewStar} alt="" />
                      <div className="d-flex gap-1">
                        <p className="fw-medium">{selectedCard?.averageRating}{" "}</p>
                        <p>({`${selectedCard?.totalReviews} Reviews`})</p>
                      </div>
                    </div>
                    <div className="RevBox">
                      <img src={ReviewUser} alt="" />
                      <div className="d-flex gap-1">
                        <p className="fw-medium">{selectedCard?.members}</p>
                        <p className="fs-12">Subscribers</p>
                      </div>
                    </div>
                  </div>
                  <div className="subAndReviewBox fs-12">
                    <div className="RevBox">
                      <img src={blockchainIcon} alt="BlockChain" />
                      <p>
                        {selectedCard?.blockchain
                          ? `${selectedCard.blockchain} Verified`
                          : "Blockchain Verified"}
                      </p>
                    </div>
                    <div className="RevBox">
                      <img src={Exchange} alt="Exchange" />
                      <p>Bybit, Binance</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA button */}
              <div className="">
                {Number(selectedCard?.communityTrial) ? (
                  <div className="btnBox">
                    <BubbleSvgButton
                      text={`Free ${selectedCard?.communityTrial}-day trial`}
                      type="button"
                      variant="black"
                      containerStyling={{ justifyContent: "center" }}
                      onClick={handleClose}
                    />
                    <p className="fs-10 text-center mt-1">100% Automated. Cancel Anytime.</p>
                  </div>
                ) : (
                  <BubbleSvgButton
                    text="Start"
                    type="button"
                    variant="green"
                    containerStyling={{ justifyContent: "center" }}
                    onClick={handleClose}
                  />
                )}
              </div>
              
            </div>
          </div>

          <hr className="firstLine" />

          {/* ── Chart section ──────────────────────────────────────────── */}
          <div className="GraphSecondRow">
            <div className="GraphMainBox">
              <div className="topTradingBox">
                <div className="contentSide">
                  <p className="name">Overall Performance</p>
                  <p className="trading__date fs-12">{getDateRange()}</p>
                </div>
                <div className="switchSide marketPlace">
                  {/* <div className="switchBase">
                    {["Day", "Week", "Month", "All Time"].map((btn) => (
                      <button key={btn} className={activeButton === btn ? "active" : ""} onClick={() => setActiveButton(btn)}>
                        {btn === "Day" ? "Daily" : btn === "Week" ? "Weekly" : btn === "Month" ? "Monthly" : "All Time"}
                      </button>
                    ))}
                  </div> */}
                  <CommunityAreaChartTabs activeTab={activeTab} onTabChange={setActiveTab} />
                </div>
              </div>

              <div className="chartBox market_chart">
                {/* <CommunityBarChart key={selectedCard?.leaderUser?._id ? `${selectedCard.leaderUser._id}-${activeButton}` : activeButton} graphType={"pnl"} userId={userId} duration={activeButton.toLocaleLowerCase()} /> */}
                <CommunityAreaChart
          bare
          graphType="pnl"
                  userId={userId}
                  activeTab={activeTab}
                  onTabChange={setActiveTab}
                />
              </div>
            </div>
          </div>

          <hr className="firstLine two" />

          {/* ── Stats swiper ───────────────────────────────────────────── */}
          <div className="communityStatSliderWrapper">
            <h5 className="fw-medium mb-3">Statistics</h5>
            <Swiper
              pagination={{ clickable: true }}
              modules={[Pagination]}
              className="communityStatSwiper"
              slidesPerView="auto"
              spaceBetween={23}
            >
              {/* Group ranking slide */}
              <SwiperSlide className="communityStatSwiper__rankingSlide">
                <div className="communityStatItem groupRankingStatItem">
                  <p className="fw-medium fs-18">Group Ranking</p>
                  <p className="fs-12">Among all trading groups</p>
                  <p className="fw-medium fs-22 d-flex justify-content-end">
                    {selectedCard?.communityRanking || "-"}&nbsp;/&nbsp;{selectedCard?.totalCommunities || "-"}
                  </p>
                </div>
              </SwiperSlide>

              {/* Stat slides */}
              {getStatsDataFromCard(selectedCard).map((stat, index) => (
                <SwiperSlide key={index}>
                  <div
                    className="communityStatItem"
                    style={{
                      minHeight: "84.71px",
                      borderRadius: "10px",
                      backgroundColor: "#f7f7f7",
                      boxShadow: "0px 15px 24px rgba(0, 0, 0, 0.05)",
                      padding: "13.7px 13.5px",
                    }}
                  >
                    <p className="title" style={{ fontSize: "12px", fontFamily: "Graphik Regular", marginBottom: 0, height: 32 }}>
                      {stat.title}
                    </p>
                    <p className="numbers" style={{ fontSize: "22px", fontFamily: "Graphik Medium", marginBottom: 0 }}>
                      {stat.numbers}
                    </p>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
};

export default DisplayModal;
